/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import * as React from 'react';
import * as PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { getAEMResource, getHomepage } from '../../selectors/generic';
import { isLoadingById } from '../../selectors/loader';
import { withI18N } from '../hoc/withI18N';
import { RecipeListModelType } from '../../data/recipe-list.model';
import RecipeListGroup from '../molecules/recipes/RecipeListGroup';
import { Button } from '../atoms/bootstrap/Button';
import { MultiProductOrderingModalId } from '../organisms/modals/MultiProductOrderingModal';
import { Glyphicons, Icon } from '../atoms/bootstrap/Icons';
import { openModal } from '../../actions/modals';
import { isLoggedIn, ROLES } from '../../data/user.model';
import { redirectToLogin } from '../../actions/auth';
import { setProductImpressions } from '../../actions/my-puratos/myProducts.action';
import { RoleMatrix } from '../molecules/ecom/RoleMatrix';
import { SkeletonTypes } from '../atoms/effects/skeletons/utils/SkeletonTypes';
import { WithSkeleton } from '../my-puratos/hoc/WithSkeleton';
import { GA4CrossSellComponentType } from '../../analytics/ga4/types/component';
import { GA4InteractionType } from '../../analytics/ga4/types/interaction';

class RecipeList extends React.Component {
  static propTypes = {
    resource: PropTypes.shape(RecipeListModelType),
    resourceLoading: PropTypes.bool
  };

  static defaultProps = {
    resourceLoading: true
  };

  componentDidMount() {
    this.setImpressions(this.props.resource);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resource !== this.props.resource) {
      this.setImpressions(this.props.resource);
    }
  }

  setImpressions = resource => {
    let puratosProducts = resource.groups?.map(group =>
      group.ingredients
        ?.filter(ingredient => ingredient.productDetails !== null)
        .map(ingredient => {
          return { ...ingredient.productDetails, title: ingredient.ingredientLabel };
        })
    );

    if (puratosProducts) {
      const mergedGroups = [].concat.apply([], puratosProducts);
      this.props.setProductImpressions({
        products: mergedGroups,
        list: GA4CrossSellComponentType.RECIPE_LIST,
        type: GA4InteractionType.recipe
      });
    }
  };

  onClick = e => {
    if (e) {
      e.preventDefault();
    }

    if (!isLoggedIn()) {
      this.props.redirectToLogin();
      return;
    }

    const products = this.props.resource?.linkedProductsIngredientModel?.filter(
      product => product.ingredientType === 'LINK'
    );
    const productIds = products
      ?.map(({
               productDetails,
               productReference
             }) => productDetails?.productId || productReference?.id)
      .filter(id => !!id);

    const addAll = true,
      hideCartAmountAlert = true,
      showBundles = true;
    const translate = this.props.translate;

    productIds.length &&
    this.props.openModal(MultiProductOrderingModalId, {
      productIds: [...new Set(productIds)],
      hideCartAmountAlert,
      addAll,
      showBundles,
      translate,
      type: GA4InteractionType.recipe
    });
  };

  render() {
    const { resourceLoading, resource: ingredientsListModel, homepage, translate } = this.props;

    if (!resourceLoading && (!ingredientsListModel || ingredientsListModel.empty)) {
      return null;
    }

    const hasProducts = ingredientsListModel?.linkedProductsIngredientModel?.length > 0 || false;

    return (
      <React.Fragment>
        <WithSkeleton loading={resourceLoading} skeletonType={SkeletonTypes.RECIPE_LIST}>
          <div className="well p-margin-none">
            <table className="table table-condensed p-margin-none">
              <thead>
              <tr>
                <th width="60%"></th>
                <th
                  width={
                    ingredientsListModel.showPercentage &&
                    ingredientsListModel.percentageHeader != null
                      ? '25%'
                      : '40%'
                  }
                  className="text-right p-table-text--header">
                  {ingredientsListModel.unitsHeader}
                </th>
                {ingredientsListModel.showPercentage &&
                  ingredientsListModel.percentageHeader != null && (
                    <th width="15%" className="text-right p-table-text--header">
                      {ingredientsListModel.percentageHeader}
                    </th>
                  )}
              </tr>
              </thead>

              <tbody>
              {ingredientsListModel?.groups?.map(group => (
                <RecipeListGroup
                  key={group.groupTotalLabel}
                  group={group}
                  translate={translate}
                  homepage={homepage}
                  showPercentageValues={ingredientsListModel.showPercentage}
                />
              ))}
              </tbody>

              {ingredientsListModel.ingredientsRemark && (
                <tfoot>
                <tr>
                  <td className="p-table-text--footer">
                    <span>*</span> {ingredientsListModel.ingredientsRemark}
                  </td>
                  <td className="text-right p-table-text--footer">
                    {ingredientsListModel.ingredientRemarkUnits}
                  </td>
                  {ingredientsListModel.showPercentage &&
                    ingredientsListModel.percentageHeader != null && (
                      <td className="text-right p-table-text--footer">
                        {ingredientsListModel.ingredientRemarkPercentage}
                      </td>
                    )}
                </tr>
                </tfoot>
              )}
            </table>
          </div>
          <RoleMatrix>
            <RoleMatrix.Fragment roles={[ROLES.valid, ROLES.new, ROLES.loggedOut]}>
              {homepage.enableRecipeAddToCart && hasProducts && (
                <Button
                  color={Button.colors.SECONDARY}
                  onClick={() => this.onClick()}
                  className={'p-margin-top-md p-padding-right-md'}>
                  <Icon
                    type={Glyphicons.shoppingCart}
                    className={'p-margin-right-sm p-margin-left-sm'}
                  />{' '}
                  {translate('Add all Puratos ingredients')}
                </Button>
              )}
            </RoleMatrix.Fragment>
          </RoleMatrix>
        </WithSkeleton>
      </React.Fragment>
    );
  }
}

export default compose(
  connect(
    (state, ownProps) => ({
      resource: getAEMResource(state, ownProps.model) || {},
      homepage: getHomepage(state),
      resourceLoading: isLoadingById(state, ownProps.model)
    }),
    { openModal, redirectToLogin, setProductImpressions }
  ),
  withI18N
)(RecipeList);
